import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, HelpCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { apiService } from '../../services/apiService';
import { useWalletTopLayerDialog } from '../../hooks/useWalletTopLayerDialog';

export interface HowToModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic: string | null;
  fallbackTitle?: string;
}

function stripAudiencePrefix(text: string): string {
  return text.replace(/^\*\*Audience:\s*[^*]+\*\*\s*/i, '').trim();
}

function renderInlineBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={`b-${index}`} className="font-semibold text-gray-900">
          {boldMatch[1]}
        </strong>
      );
    }
    return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
  });
}

function HelpBody({ body }: { body: string }) {
  const cleaned = stripAudiencePrefix(body);
  const blocks = cleaned.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);

  return (
    <div className="space-y-4">
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        const bulletLines = lines.filter((line) => /^[-•]\s+/.test(line));
        const isBulletBlock = bulletLines.length > 0 && bulletLines.length === lines.length;

        if (isBulletBlock) {
          return (
            <ul key={`ul-${blockIndex}`} className="list-none space-y-3 pl-0">
              {lines.map((line, lineIndex) => {
                const item = line.replace(/^[-•]\s+/, '');
                return (
                  <li key={`li-${blockIndex}-${lineIndex}`} className="flex gap-2 text-sm leading-relaxed text-gray-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#EAB308]" aria-hidden />
                    <span>{renderInlineBold(item)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }

        return (
          <p key={`p-${blockIndex}`} className="text-sm leading-relaxed text-gray-700">
            {renderInlineBold(lines.join(' '))}
          </p>
        );
      })}
    </div>
  );
}

export function HowToModal({ isOpen, onClose, topic, fallbackTitle }: HowToModalProps) {
  const [title, setTitle] = useState(fallbackTitle || 'How it works');
  const [sections, setSections] = useState<Array<{ heading: string; body: string }>>([]);
  const [links, setLinks] = useState<Array<{ label: string; url: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useWalletTopLayerDialog(isOpen);

  useEffect(() => {
    if (!isOpen || !topic) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const payload = await apiService.getHowToTopic(topic);
        if (cancelled) return;
        setTitle(payload.title || fallbackTitle || 'How it works');
        setSections(payload.sections || []);
        setLinks(payload.links || []);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load help content.');
        setSections([]);
        setLinks([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, topic, fallbackTitle]);

  useEffect(() => {
    if (!isOpen) {
      setError('');
      setSections([]);
      setLinks([]);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="how-to-modal flex flex-col gap-0 overflow-hidden p-0"
        closeButtonClassName="!top-3 !right-3 !bg-gray-100 hover:!bg-gray-200"
        style={{
          fontFamily: 'Montserrat, sans-serif',
          position: 'fixed',
          left: '50%',
          top: 'auto',
          bottom: 'calc(var(--wallet-bottom-nav-height) + 16px)',
          transform: 'translateX(-50%)',
          width: 'min(calc(100% - 2rem), 340px)',
          maxHeight: 'min(72vh, calc(100vh - 112px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.18)',
          backgroundColor: '#ffffff',
        }}
      >
        <DialogHeader className="shrink-0 border-b border-gray-100 px-5 pb-4 pt-5 text-left">
          <div className="flex items-start gap-3 pr-8">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: 'rgba(234, 179, 8, 0.15)' }}
            >
              <HelpCircle className="h-5 w-5 text-[#CA8A04]" strokeWidth={2.25} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base font-bold leading-snug text-gray-900">
                {title}
              </DialogTitle>
              <p className="mt-1 text-xs text-gray-500">How it works</p>
            </div>
          </div>
          <DialogDescription className="sr-only">
            Step-by-step help for {title}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading help...
            </div>
          )}

          {!isLoading && error && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!isLoading && !error && sections.length === 0 && (
            <p className="py-6 text-sm text-gray-600">
              Help content is not available right now. Please try again later or contact support.
            </p>
          )}

          {!isLoading && !error && sections.length > 0 && (
            <div className="space-y-6">
              {sections.map((section) => (
                <section key={section.heading}>
                  {sections.length > 1 && section.heading !== title && (
                    <h3 className="mb-3 text-sm font-semibold text-gray-900">{section.heading}</h3>
                  )}
                  <HelpBody body={section.body} />
                </section>
              ))}
            </div>
          )}

          {!isLoading && links.length > 0 && (
            <div className="mt-5 flex flex-col gap-2 border-t border-gray-100 pt-4">
              {links.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#2D8CCA] hover:underline"
                >
                  {link.label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-11 w-full border-gray-300 text-gray-800 hover:bg-gray-50"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
